import { useRef, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';

import {
  ChatExploration,
  ChatMessage,
  ChatMode,
  ChatUserMessage,
  Cell,
  CanvasState,
  ConversationJob,
} from '@/explore/types';
import { Breakpoint, useScreenSize } from '@/lib/hooks';
import { ConversationExplorationContextProvider } from '@/explore/conversation/conversation-exploration-context';
import { ErrorBanner } from '@/components/banner';
import { measureCanvasNodes } from '@/explore/components/canvas/utils';
import { isUserMessage } from '@/core/cell';
import { getLastExplorationCellId } from '@/core/conversation';
import { setExplorationCanvas, setCanvasNodes } from '@/core/exploration';
import { useConversationRepositoryContext } from '@/explore/conversation/conversation-repository-context';
import { useTrackEvent } from '@/lib/analytics';
import { generateUUID, isNil } from '@/lib/utils';

import { useBuildAccountUrl } from '@/lib/accounts/context';

import { buildExplorationUrl } from '@/explore/utils';

import { Loader } from '@/components/loader';

import { getJobStatus } from '@/core/conversation/job';

import { useConversation } from './use-conversation';
import { ExplorationCell } from '../exploration-cell';
import { ChatMessages } from '../chat-cell/components/chat-messages';
import { UserMessageInput } from '../chat-cell/components/user-message-input';
import { useExplorationUIContext } from '../exploration-ui-context';
import { useExplorationContext } from '../exploration-context';
import { useExplorationCellContext } from '../exploration-cell-context';

import styles from './conversation-cell.module.scss';
import notebookStyles from '../notebook/exploration-notebook.module.scss';

type MessageGroup =
  | {
      type: 'exploration';
      messages: ChatExploration[];
    }
  | {
      type: 'messages';
      messages: ChatMessage[];
    };

type ConversationCellViewProps = {
  conversationId: string;
};

export const ConversationCellView = (props: ConversationCellViewProps) => {
  const { conversationId } = props;
  const { error, refetch } = useConversation(conversationId);
  const {
    getConversation,
    addUserMessage,
    updateUserMessage,
    cancelConversationJob,
    rerunConversationJob,
    discardMessage,
    extractExplorationCells,
    extractExplorationCellsAfterMessage,
  } = useConversationRepositoryContext();
  const screenSize = useScreenSize();
  const buildAccountUrl = useBuildAccountUrl();
  const desktopView = screenSize.breakpoint > Breakpoint.md;
  const { exploration, removeConversation, acceptConversation } = useExplorationContext();
  const { endConversation } = useExplorationCellContext();
  const { selectedCell, openEditor, scrollToCell, highlightedCellId } = useExplorationUIContext();
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState<string | undefined>();
  const trackEvent = useTrackEvent();
  const [prevLastExplorationCellId, setPrevLastExplorationCellId] = useState<
    string | null | undefined
  >(undefined);

  const conversation = getConversation(conversationId);
  const canvasView = useMemo(
    () => exploration.view.options?.layout === 'canvas',
    [exploration.view.options],
  );

  useEffect(() => {
    const lastExplorationCellId = getLastExplorationCellId(conversation);
    if (lastExplorationCellId !== prevLastExplorationCellId) {
      setPrevLastExplorationCellId(lastExplorationCellId);
      // If a new exploration cell has been added, scroll to it
      if (prevLastExplorationCellId !== undefined && !isNil(lastExplorationCellId)) {
        scrollToCell(lastExplorationCellId);
      }
    }
  }, [conversation, prevLastExplorationCellId, scrollToCell]);

  const handleDiscardAll = () => {
    removeConversation(conversationId);
    trackEvent('Conversation Discard All', { conversationId });
  };

  if (error !== undefined) {
    return (
      <ErrorBanner
        title={'Failed to load conversation'}
        actions={[
          { label: 'Retry', onClick: () => refetch() },
          {
            label: 'Remove conversation',
            onClick: () => handleDiscardAll(),
          },
        ]}
      />
    );
  }

  if (conversation === undefined) {
    return (
      <div className={styles.loading}>
        <Loader size="medium" />
      </div>
    );
  }

  const clearDraft = () => {
    setDraft(undefined);
  };

  const updateCellSizes = (cells: Cell[]): CanvasState => {
    const persistedNodes = exploration.view.canvas?.nodes ?? [];

    if (!canvasView || !messagesRef.current) {
      return setCanvasNodes(exploration.view.canvas, persistedNodes);
    }

    const persistedNodeIds = persistedNodes.map((node) => node.id);
    const nodes = measureCanvasNodes(
      messagesRef.current,
      cells.map((cell) => cell.id),
    ).filter((node) => !persistedNodeIds.includes(node.id));
    return setCanvasNodes(exploration.view.canvas, persistedNodes.concat(nodes));
  };

  const handleDiscard = () => {
    const lastUserMessage = conversation.messages.findLast(isUserMessage);
    if (lastUserMessage !== undefined) {
      setDraft(lastUserMessage.message);
      discardMessage(conversationId, lastUserMessage.id);
      trackEvent('Conversation Discard Last', { conversationId });
    }
  };

  const handleAcceptLast = (event?: React.MouseEvent) => {
    event?.preventDefault();
    const lastUserMessage = conversation.messages.findLast(isUserMessage);
    if (lastUserMessage === undefined) {
      throw new Error('Cannot find blocks to accept');
    }
    const cells = extractExplorationCellsAfterMessage(conversationId, lastUserMessage.id);
    const canvasState = updateCellSizes(cells);
    acceptConversation(conversationId, cells, setExplorationCanvas(exploration, canvasState));
    trackEvent('Conversation Accept Last', { conversationId });
  };

  const handleAcceptAll = (event?: React.MouseEvent) => {
    event?.preventDefault();
    const cells = extractExplorationCells(conversationId);
    const canvasState = updateCellSizes(cells);
    acceptConversation(conversationId, cells, setExplorationCanvas(exploration, canvasState));
    trackEvent('Conversation Accept All', { conversationId });
  };

  const groupedMessages = conversation.messages.reduce<MessageGroup[]>((groups, message) => {
    const lastGroup = groups.at(-1);

    if (message.type === 'exploration') {
      if (lastGroup === undefined || lastGroup.type === 'messages') {
        groups.push({
          type: 'exploration',
          messages: [message],
        });
      } else if (lastGroup.type === 'exploration') {
        lastGroup.messages.push(message);
      }
    } else {
      if (lastGroup === undefined || lastGroup.type === 'exploration') {
        groups.push({
          type: 'messages',
          messages: [message],
        });
      } else if (lastGroup.type === 'messages') {
        lastGroup.messages.push(message);
      }
    }

    return groups;
  }, []);
  const explorationReplies = groupedMessages.filter((group) => group.type === 'exploration');

  const getGroupJobs = (jobs: ConversationJob[], messages: ChatMessage[]) => {
    const messageJobIds = new Set(
      messages
        .map((message) => message.jobId)
        .filter((jobId): jobId is string => jobId !== undefined),
    );

    const jobMap = new Map(jobs.map((job) => [job.jobId, job]));

    const belongsToGroup = (job: ConversationJob): boolean =>
      messageJobIds.has(job.jobId) ||
      (job.parentJobId !== undefined &&
        jobMap.has(job.parentJobId) &&
        belongsToGroup(jobMap.get(job.parentJobId)!));

    return jobs.filter(belongsToGroup);
  };

  const handleAddUserMessage = async (content: string, sources: string[], mode: ChatMode) => {
    const correlationId = generateUUID();
    const updatedExploration = await addUserMessage(
      conversationId,
      content,
      sources,
      mode,
      correlationId,
    );

    if (updatedExploration !== null) {
      clearDraft();
      trackEvent('Conversation Add User Message', {
        message: content,
        conversationId,
        correlationId,
        originatingExplorationUrl: buildAccountUrl(buildExplorationUrl(exploration)),
        resultExplorationUrl: buildAccountUrl(buildExplorationUrl(updatedExploration)),
        isResearch: mode === 'research',
        sources,
        hasCurrentExploration: exploration.view.cells.length > 0,
      });
    }
  };

  const handleUpdateUserMessage = async (message: ChatUserMessage) => {
    const correlationId = generateUUID();
    const updatedExploration = await updateUserMessage(
      conversationId,
      message.id,
      message,
      correlationId,
    );

    if (updatedExploration !== null) {
      clearDraft();
      trackEvent('Conversation Update User Message', {
        message: message.message,
        conversationId,
        correlationId,
        originatingExplorationUrl: buildAccountUrl(buildExplorationUrl(exploration)),
        resultExplorationUrl: buildAccountUrl(buildExplorationUrl(updatedExploration)),
        isResearch: message.type === 'clarifying_answer' ? undefined : message.research,
        sources: message.type === 'clarifying_answer' ? undefined : message.sources,
        hasCurrentExploration: exploration.view.cells.length > 0,
      });
    }
  };

  const hasMessages = conversation.messages.length > 0;
  const isUserInputVisible =
    conversation.jobs.every((job) => getJobStatus(job) !== 'running') &&
    groupedMessages.at(-1)?.type !== 'messages';
  const runningJobs = conversation.jobs.filter((job) => getJobStatus(job) === 'running');
  const latestJob = runningJobs.at(-1);

  const lastGroup = groupedMessages.at(-1);
  const lastMessage = lastGroup?.messages.at(-1);
  const allowDiscardingInitialMessage =
    lastGroup?.type === 'messages' &&
    lastGroup.messages.length === 1 &&
    lastMessage?.role === 'user' &&
    (latestJob === undefined || getJobStatus(latestJob) === 'idle');

  return (
    <ConversationExplorationContextProvider conversationId={conversationId}>
      {groupedMessages.length > 0 && (
        <div ref={messagesRef} className={styles.messages}>
          {groupedMessages.map((group, idx) => {
            switch (group.type) {
              case 'messages': {
                // Assuming the current UI only allows control over the last job in the message group
                // and all previous jobs are settled (no parallel jobs)
                const latestJobId = group.messages.findLast(
                  (message) => message.jobId !== undefined,
                )?.jobId;
                const latestJob = conversation.jobs.find((job) => job.jobId === latestJobId);
                return (
                  <div key={idx} className={styles.message}>
                    <ChatMessages
                      messages={group.messages}
                      editableInitialMessage={allowDiscardingInitialMessage}
                      jobs={getGroupJobs(conversation.jobs, group.messages)}
                      onAbort={(jobId) => {
                        const jobMessage = conversation.messages.find(
                          (message) => message.jobId === jobId,
                        );
                        if (jobMessage !== undefined && isUserMessage(jobMessage)) {
                          setDraft(jobMessage.message);
                        }
                        cancelConversationJob(conversationId, jobId);

                        if (explorationReplies.length === 0) {
                          handleDiscardAll();
                        }
                      }}
                      onAccept={(partial) =>
                        partial === true ? handleAcceptLast() : handleAcceptAll()
                      }
                      onDiscard={(partial) =>
                        partial === true ? handleDiscard() : handleDiscardAll()
                      }
                      onCancel={() =>
                        conversation.messages.filter(isUserMessage).length > 1
                          ? handleDiscard()
                          : handleDiscardAll()
                      }
                      onSubmit={handleAddUserMessage}
                      onMessageChange={handleUpdateUserMessage}
                      onRetry={() => {
                        if (latestJob === undefined) {
                          throw new Error('No job to retry');
                        }
                        rerunConversationJob(conversationId, latestJob.jobId);
                      }}
                    />
                  </div>
                );
              }
              case 'exploration':
                return (
                  <div
                    key={idx}
                    className={classNames({
                      [styles.responseGroup]: true,
                      [styles.borderlessGroup]: canvasView,
                      [styles.lastResponse]: idx === groupedMessages.length - 1,
                    })}>
                    {group.messages.map(({ exploration }) => (
                      <div key={exploration.explorationId} className={classNames(styles.message)}>
                        {exploration.view.cells.map((cell: any) => {
                          const isSelected = cell.id === selectedCell?.id;
                          return (
                            <ExplorationCell
                              cell={cell}
                              key={cell.id}
                              parameters={{}}
                              index={0}
                              selected={isSelected}
                              highlighted={cell.id === highlightedCellId}
                              variables={[]}
                              isCollapsible={false}
                              isResizable={false}
                              canvasView={canvasView}
                              onSelectCell={() => !isSelected && desktopView && openEditor(cell.id)}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
            }
          })}
        </div>
      )}
      {isUserInputVisible && (
        <div
          className={classNames(styles.messageInput, notebookStyles.conversationActive, {
            [notebookStyles.conversationLine]: hasMessages,
          })}>
          {hasMessages ? (
            <UserMessageInput
              placeholder="Follow up or request edits"
              initialValue={draft}
              onSubmit={handleAddUserMessage}
              onAccept={handleAcceptLast}
              primaryActions={(value, sources, mode) =>
                value.length > 0
                  ? [{ label: 'Ask', onClick: () => handleAddUserMessage(value, sources, mode) }]
                  : explorationReplies.length === 1
                    ? [{ label: 'Accept', onClick: handleAcceptAll }]
                    : [
                        { label: 'Accept last', onClick: handleAcceptLast },
                        { label: 'Accept all blocks', onClick: handleAcceptAll },
                      ]
              }
              secondaryActions={
                explorationReplies.length === 1
                  ? [{ label: 'Discard conversation', onClick: handleDiscardAll }]
                  : [
                      { label: 'Discard last', onClick: handleDiscard },
                      {
                        label: 'Discard conversation',
                        onClick: handleDiscardAll,
                      },
                    ]
              }
              autoFocus={!canvasView}
            />
          ) : (
            <UserMessageInput
              title="Question"
              placeholder="Type to ask AI a question"
              initialValue={draft}
              onSubmit={handleAddUserMessage}
              onCancel={endConversation}
              onAccept={handleAcceptLast}
              primaryActions={(value, sources, mode) => [
                {
                  label: 'Send',
                  disabled: value.length === 0,
                  onClick: () => handleAddUserMessage(value, sources, mode),
                },
              ]}
              secondaryActions={[{ label: 'Discard (Esc)', onClick: endConversation }]}
              autoFocus={!canvasView}
            />
          )}
        </div>
      )}
    </ConversationExplorationContextProvider>
  );
};
